import { memo, useState } from "react";
import { List as TabsList, Root as TabsRoot } from "@radix-ui/react-tabs";
import type { TabsProps as RadixTabsProps } from "@radix-ui/react-tabs";
import { LayoutGroup, motion } from "framer-motion";
import { cn } from "@/lib/utils";
import TabButton from "./components/TabButton";
import Icon from "@/components/ui/Icon";

const tabsIconsMap = {
  standard: "icon-file-description_25",
  certified: "icon-certificate_25",
  enterprise: "icon-building-skyscraper_25",
} as const;

type TabKey = keyof typeof tabsIconsMap;

export type TabsTrigger = {
  key: string;
  label: string;
};

export type TabsProps = {
  tabsTriggersSlot?: React.ReactNode;
  tabsTriggers?: TabsTrigger[];
  tablistClassName?: string;
  tablistWrapClassName?: string;
  tabTriggerClassName?: string;
  tabIndicatorClassName?: string;
} & React.ComponentProps<"div"> &
  RadixTabsProps;

const ActiveTabIndicator: React.FC<React.ComponentProps<"span">> = ({
  className,
}) => {
  return (
    <motion.span
      layoutId="activeTab"
      className={cn("w-full h-full absolute left-0 top-0 shadow-[0_8px_16px_0_rgba(0,0,0,0.16)]", className)}
      aria-hidden="true"
      role="presentation"
    />
  );
};

const Tabs: React.FC<TabsProps> = ({
  className,
  defaultValue,
  tabsTriggers,
  tabsTriggersSlot,
  tablistClassName,
  tabTriggerClassName,
  tablistWrapClassName,
  tabIndicatorClassName = "hidden",
  children,
  ...rest
}) => {
  const [activeTab, setActiveTab] = useState(defaultValue);

  return (
    <TabsRoot
      className={cn(className)}
      value={activeTab}
      onValueChange={setActiveTab}
      defaultValue={defaultValue}
      {...rest}
    >
      <div className="flex items-center justify-center">
        <div
          className={cn(
            "flex min-w-0 items-center px-6 py-1 -mx-6 -my-1 no-scrollbar",
            tablistWrapClassName
          )}
        >
          <LayoutGroup>
            <TabsList
              className={cn("inline-flex items-center gap-1", tablistClassName)}
            >
              {!!tabsTriggers?.length
                ? tabsTriggers.map((item, i) => {
                    const iconId = tabsIconsMap[item.key as TabKey];
                    return (
                      <TabButton
                        key={i}
                        className={cn(tabTriggerClassName)}
                        value={item.key}
                        data-tab-label={item.label}
                      >
                        {!!iconId && (
                          <Icon
                            id={iconId}
                            width={25}
                            height={25}
                            className="shrink-0 mr-2 hidden sm:block z-[2]"
                          />
                        )}
                        <span className="relative z-[2] will-change-[font-weight] transition-[font-weight]">
                          {item.label}
                          <span
                            className="opacity-0 block h-0 pointer-events-none overflow-hidden"
                            aria-hidden="true"
                          >
                            {item.label}
                          </span>
                        </span>
                        {activeTab === item.key && (
                          <ActiveTabIndicator
                            className={tabIndicatorClassName}
                          />
                        )}
                      </TabButton>
                    );
                  })
                : tabsTriggersSlot}
            </TabsList>
          </LayoutGroup>
        </div>
      </div>
      <div className="content relative">{children}</div>
    </TabsRoot>
  );
};

export default memo(Tabs);
